import Badge from "components/Badge";
import React from "react";
import LoadingIcon from "components/LoadingIcon";
import { IconDeviceFloppy, IconSparkles } from "@tabler/icons-react";
import clsx from "clsx";
import { XIcon } from "@heroicons/react/outline";
import {
  InventoryPhysicalObjectDetailOut,
  InventoryProductOut,
} from "api-client";
import { observer } from "mobx-react-lite";
import ProductDetailsModal from "features/ProductDetailsModal";
import Tooltip from "components/Tooltip";
import useMappingEditor from "features/MappingEditor/useMappingEditor";

interface DetailsMappingChipProps {
  object: InventoryPhysicalObjectDetailOut;
  product: InventoryProductOut;
  key?: number;
  unmatch?: () => Promise<void>;
  loading?: boolean;
  className?: string;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}

export default observer(function ViewerMappingChip({
  object,
  product,
  key,
  unmatch,
  loading,
  className,
  setLoading,
}: Readonly<DetailsMappingChipProps>) {
  const [openDetails, setOpenDetails] = React.useState(false);
  const { getMappingChipTooltipContent, getMappingChipIcon } =
    useMappingEditor();
  const isCompliant = product.is_compliant_by_file ?? false;

  const showDetails = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    setOpenDetails(true);
  };

  const closeDetails = () => {
    setOpenDetails(false);
  };

  return (
    <>
      <div
        className="flex justify-start max-w-[80%] items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip
          content={getMappingChipTooltipContent(isCompliant)}
          tooltipClassName="bg-white shadow-lg !p-4 rounded-md min-w-[700px]"
        >
          <Badge
            key={key}
            type={isCompliant ? "success" : "warning"}
            className="text-xs text-gray-700 !px-2"
          >
            <div
              onClick={showDetails}
              className="flex items-center overflow-hidden"
            >
              <div className="pr-1">
                {getMappingChipIcon(isCompliant, product.type)}
              </div>
              <div className={clsx("truncate cursor-pointer", className)}>
                {product.name ?? "-"}
              </div>
            </div>

            {!loading && unmatch ? (
              <XIcon
                width={15}
                className={clsx(
                  "ml-2 cursor-pointer min-w-[15px] max-w-[15px]",
                  {
                    "text-red-700 hover:text-red-500": !isCompliant,
                    "text-green-700 hover:text-green-500": isCompliant,
                  }
                )}
                onClick={async () => {
                  setLoading?.(true);
                  await unmatch?.();
                  setLoading?.(false);
                }}
              />
            ) : (
              loading &&
              unmatch && (
                <LoadingIcon
                  className={clsx("ml-2", {
                    "!text-red-500": !isCompliant,
                    "!text-green-500": isCompliant,
                  })}
                />
              )
            )}
          </Badge>
        </Tooltip>

        {object.product_match_type === "auto" ? (
          <div className="px-1">
            <IconSparkles className="text-gray-400 w-5" />
          </div>
        ) : null}
        {object.product_match_type === "pre" ? (
          <div className="px-1">
            <IconDeviceFloppy className="text-gray-400 w-5" />
          </div>
        ) : null}
      </div>

      <ProductDetailsModal
        productId={product.id}
        productName={product.name}
        productType={product.type}
        open={openDetails}
        close={closeDetails}
      />
    </>
  );
});
